// =============================================================================
// SiteBarsChart — barras horizontales SVG para comparativa entre centros.
// Reutilizable para facturación o deuda por centro.
// =============================================================================

import type { SiteSummary } from '../../../types/dashboard';

interface SiteBarsChartProps {
  data: SiteSummary[];
  /** Qué métrica pintar */
  metric: 'revenue' | 'openDebt' | 'budgetsCreated' | 'acceptanceRate';
  color?: string;
  formatValue?: (v: number) => string;
}

const METRIC_LABELS: Record<SiteBarsChartProps['metric'], string> = {
  revenue: 'Facturación',
  openDebt: 'Deuda abierta',
  budgetsCreated: 'Presupuestos creados',
  acceptanceRate: 'Tasa aceptación',
};

export function SiteBarsChart({
  data,
  metric,
  color = '#008DD0',
  formatValue = (v) => String(v),
}: SiteBarsChartProps) {
  if (data.length === 0) {
    return (
      <div className="text-sm text-slate-400 py-6 text-center">
        Sin datos de centros.
      </div>
    );
  }

  const values = data.map((s) => s[metric]);
  const max = Math.max(1, ...values);

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-slate-700">
        {METRIC_LABELS[metric]} por centro
      </h3>
      <ul className="space-y-2">
        {data.map((s) => {
          const v = s[metric];
          const pct = Math.max(1, (v / max) * 100); // mínimo 1% para que se vea la etiqueta
          return (
            <li key={s.siteId}>
              <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                <span className="font-medium text-slate-700 truncate">
                  {s.siteName}
                </span>
                <span className="font-semibold text-slate-900">
                  {formatValue(v)}
                </span>
              </div>
              <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: color,
                  }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
